import { ESubjects, EActions } from "@/api/models/auth/ability";
import { ETabs } from "@/api/shared/administrative";
import { usePermissionMatrix, useHasAnyPermission } from "./usePermissionHelpers";
import { useAbility } from "@casl/vue";
import { computed } from "vue";

/**
 * Section permissions composable for system-wide access control
 * Provides permissions for different application sections that require multiple permissions
 */
export const useSectionPermissions = () => {
  // Administration wizard step permissions
  const wizardStepPermissions = usePermissionMatrix(
    EActions.CREATE,
    [
      ESubjects.PRACTICE__MSO,
      ESubjects.PRACTICE__COMPANY,
      ESubjects.PRACTICE__COMPANY,
      // ESubjects.PRACTICE__CONTRACT,
      ESubjects.PRACTICE__PRACTICE,
      ESubjects.PRACTICE__PRACTICE,
      // ESubjects.PRACTICE__CONTRACT,
      ESubjects.PRACTICE__PRACTICE,
      ESubjects.PRACTICE__PRACTICESTAFF,
      ESubjects.REPORT__REPORT,
    ],
    Object.values(ETabs).slice(1) // Remove ASSISTANT tab
  );

  // Administration tab read permissions
  const administrationTabPermissions = usePermissionMatrix(
    EActions.READ,
    [
      ESubjects.PRACTICE__MSO,
      ESubjects.PRACTICE__COMPANY,
      ESubjects.PRACTICE__COMPANY,
      // ESubjects.PRACTICE__CONTRACT,
      ESubjects.PRACTICE__PRACTICE,
      ESubjects.PRACTICE__PRACTICE,
      // ESubjects.PRACTICE__CONTRACT,
      ESubjects.PRACTICE__PRACTICE,
      ESubjects.PRACTICE__PRACTICESTAFF,
      ESubjects.REPORT__REPORT,
    ],
    Object.values(ETabs).slice(1) // Remove ASSISTANT tab
  );

  // System settings permissions
  const systemSettingsPermissions = usePermissionMatrix(EActions.READ, [
    ESubjects.PRACTICE__PRACTICEAPIKEYSTORAGE,
    ESubjects.PRACTICE__BANK,
    ESubjects.PRACTICE__DOCTORLICENCE,
  ]);

  // Check if user can access system settings
  const hasSystemSettingsAccess = useHasAnyPermission(systemSettingsPermissions);

  // Section-level access is granted by dedicated staff rights; the per-tab and
  // per-step matrices above only narrow down what is visible inside the section.
  const { can } = useAbility();
  const canAccessAdministration = computed(() =>
    can(EActions.MANAGE_ADMINISTRATION, ESubjects.PRACTICE__PRACTICESTAFF)
  );
  const canAccessPlatformControl = computed(() =>
    can(EActions.MANAGE_PLATFORM_CONTROL, ESubjects.PRACTICE__PRACTICESTAFF)
  );

  const administrationTabs = computed(
    (): Record<ETabs, boolean> => ({
      ...(administrationTabPermissions.value as Record<Exclude<ETabs, ETabs.ASSISTANT>, boolean>),
      [ETabs.ASSISTANT]: canAccessAdministration.value,
    })
  );

  return {
    // Administration permissions
    administration: {
      wizard: wizardStepPermissions,
      tabs: administrationTabs,
      canAccess: canAccessAdministration,
    },

    // Platform control permissions
    platformControl: {
      canAccess: canAccessPlatformControl,
    },

    // System settings permissions
    systemSettings: {
      permissions: systemSettingsPermissions,
      canAccess: hasSystemSettingsAccess,
    },

    // Combined access checks
    canAccessAdministration,
    canAccessPlatformControl,
    canAccessSystemSettings: hasSystemSettingsAccess,
  };
};

/**
 * Specific composables for backward compatibility and convenience
 */

/**
 * Get permissions for wizard steps in administration
 * @returns Computed object with permission checks for each wizard step
 */
export const useWizardStepPermissions = () => {
  const { administration } = useSectionPermissions();
  return administration.wizard;
};

/**
 * Get permissions for administration tabs
 * @returns Computed object with permission checks for each administration tab
 */
export const useAdministrationTabPermissions = () => {
  const { administration } = useSectionPermissions();
  return administration.tabs;
};

/**
 * Check if user can access administration section
 * @returns Computed boolean indicating if user can access administration
 */
export const useCanAccessAdministration = () => {
  const { administration } = useSectionPermissions();
  return administration.canAccess;
};

/**
 * Check if user can access platform control
 * @returns Computed boolean indicating if user can access platform control
 */
export const useCanAccessPlatformControl = () => {
  const { platformControl } = useSectionPermissions();
  return platformControl.canAccess;
};

/**
 * Check if user can access system settings
 * @returns Computed boolean indicating if user can access system settings
 */
export const useCanAccessSystemSettings = () => {
  const { systemSettings } = useSectionPermissions();
  return systemSettings.canAccess;
};

export const useCanAccessDsoRepresentative = () => {
  const { can } = useAbility();

  return computed(() => can($permActions.VIEW_MSO_ADMIN, $permSubjects.PRACTICE__MSO));
};

export const useCanEditMso = () => {
  const { can } = useAbility();

  return computed(
    () =>
      can($permActions.UPDATE, $permSubjects.PRACTICE__MSO) ||
      can($permActions.MSO_ME_EDIT, $permSubjects.PRACTICE__MSO)
  );
};

export const useCanAccessOfficesManagement = () => {
  const { can } = useAbility();

  return computed(
    () =>
      can($permActions.COMPANY_ME_VIEW, $permSubjects.PRACTICE__COMPANY) &&
      can($permActions.PRACTICE_ME_VIEW, $permSubjects.PRACTICE__PRACTICE)
  );
};
